import { useEffect, useState, useRef } from 'react';
import api, { vehicleAPI, companyAPI, lookupAPI, referenceAPI, API_BASE } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { PageHeader, Button, Table, StatusBadge, Modal, Input, Select, Loading, Alert } from '../components/ui';

const TYPES  = ['car','van','truck','motorcycle','bus','other'];
const FUELS  = ['petrol','diesel','electric','hybrid'];
const OWN    = ['owned','leased','rented'];
const REG_STATUS = ['Brand New', 'Reconditioned', 'Used / Registered'];
const BODY_TYPES = [
  'Saloon', 'Hatchback', 'Station Wagon', 'SUV', 'Jeep',
  'Double Cab', 'Single Cab', 'Van', 'Mini Van', 'Bus',
  'Lorry', 'Truck', 'Tipper', 'Prime Mover', 'Trailer',
  'Motor Cycle', 'Three Wheeler', 'Cab', 'Other',
];

const empty = {
  reg_number:'', type:'car', brand:'', model:'', year:'', color:'',
  fuel_type:'diesel', engine_number:'', chassis_number:'', ownership:'owned', company_id:'', has_lease:false, odometer:'',
  // CR / Registration book
  cr_no:'', vehicle_class:'', status_when_registered:'', body_type:'', first_reg_date:'',
  cylinder_capacity:'', tax_class:'', country_of_origin:'', manufacturer_description:'',
  tyre_size:'', battery_size:'', battery_count:'',
  // Lease
  lease_provider:'', lease_amount:'', lease_duration:'', lease_start_date:'', lease_end_date:'', lease_status:'active', cr_transferred:'',
  // Document amounts
  revenue_licence_amount:'', emission_test_amount:'', fitness_cert_amount:'',
  // Insurance
  insurance_no:'', insurance_expiry:'',
  // Revenue Licence
  revenue_licence_no:'', revenue_licence_expiry:'',
  // Emission Test
  emission_test_no:'', emission_test_expiry:'',
  // Fitness Certificate
  fitness_cert_no:'', fitness_cert_expiry:'',
  notes:'',
};


const daysLeft = (dateStr) => {
  if (!dateStr) return null;
  return Math.ceil((new Date(dateStr) - new Date()) / 86400000);
};

const ExpiryBadge = ({ date, label }) => {
  if (!date) return <span className="text-gray-400 text-xs">—</span>;
  const d = daysLeft(date);
  const color = d < 0 ? 'bg-red-100 text-red-700' : d <= 30 ? 'bg-red-50 text-red-600' : d <= 60 ? 'bg-amber-50 text-amber-600' : 'bg-green-50 text-green-600';
  return (
    <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${color}`}>
      {date} {d < 0 ? `(${Math.abs(d)}d ago)` : `(${d}d)`}
    </span>
  );
};

export default function Vehicles() {
  const { can } = useAuth();
  const [vehicles,  setVehicles]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState('');
  const [success,   setSuccess]   = useState('');
  const [search,    setSearch]    = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [activeTab, setActiveTab] = useState('list'); // list | expiry

  // Modals
  const [formModal,   setFormModal]   = useState(false);
  const [detailModal, setDetailModal] = useState(false);
  const [uploadModal, setUploadModal] = useState(false);
  const [editing,     setEditing]     = useState(null);
  const [companies,   setCompanies]   = useState([]);
  const [lists,       setLists]       = useState({});
  const [tyreSizes,   setTyreSizes]   = useState([]);
  const [batSizes,    setBatSizes]    = useState([]);
  const [selected,    setSelected]    = useState(null);
  const [form,        setForm]        = useState(empty);
  const [saving,      setSaving]      = useState(false);

  // Upload state
  const [pdfFile,     setPdfFile]     = useState(null);
  const [imageFiles,  setImageFiles]  = useState([]);
  const [uploading,   setUploading]   = useState(false);
  const pdfRef   = useRef();
  const imgRef   = useRef();

  // Expiry list
  const [expiring,    setExpiring]    = useState([]);
  const [expLoading,  setExpLoading]  = useState(false);

  const flash = (msg, isErr = false) => {
    isErr ? setError(msg) : setSuccess(msg);
    setTimeout(() => isErr ? setError('') : setSuccess(''), 3500);
  };

  const load = () => {
    setLoading(true);
    vehicleAPI.getAll({ search, status: filterStatus, limit: 100 })
      .then(r => setVehicles(r.data.data))
      .catch(() => setError('Failed to load vehicles'))
      .finally(() => setLoading(false));
  };

  const loadExpiring = () => {
    setExpLoading(true);
    vehicleAPI.getExpiring(90)
      .then(r => setExpiring(r.data.data))
      .catch(() => flash('Failed to load expiry list', true))
      .finally(() => setExpLoading(false));
  };

  useEffect(() => { load(); }, [search, filterStatus]);
  useEffect(() => {
    companyAPI.getAll()
      .then(r => setCompanies((r.data.data || []).filter(c => c.status !== 'inactive')))
      .catch(() => setCompanies([]));
    lookupAPI.getAll()
      .then(r => setLists(r.data.data || {}))
      .catch(() => setLists({}));
    referenceAPI.getTyreSizes().then(r => setTyreSizes(r.data.data || [])).catch(() => {});
    referenceAPI.getBatterySizes().then(r => setBatSizes(r.data.data || [])).catch(() => {});
    api.get('/reference/tyre-sizes').then(r => setTyreSizes(r.data.data || [])).catch(() => {});
    api.get('/reference/battery-sizes').then(r => setBatSizes(r.data.data || [])).catch(() => {});
  }, []);

  // DB list with a built-in fallback if the lookup table isn't populated
  const list = (key, fallback) => (lists[key]?.length ? lists[key] : fallback);
  useEffect(() => { if (activeTab === 'expiry') loadExpiring(); }, [activeTab]);

  // ── Form modal ──────────────────────────────────────────────────────────────
  const openAdd  = () => { setEditing(null); setForm(empty); setFormModal(true); };
  const openEdit = v => {
    setEditing(v);
    setForm({ ...empty, ...v, year: v.year||'', odometer: v.odometer||'',
      company_id: v.company_id || '', has_lease: !!v.has_lease });
    setFormModal(true);
  };

  const toggleActive = async (v) => {
    const goingInactive = v.status !== 'inactive';
    const msg = goingInactive
      ? `Deactivate ${v.reg_number}?\n\nIt will no longer be available for assignments, repair requests, fuel logs or fuel cards.`
      : `Reactivate ${v.reg_number}?`;
    if (!window.confirm(msg)) return;
    try {
      await vehicleAPI.setStatus(v.id, goingInactive ? 'inactive' : 'active');
      flash(goingInactive ? `${v.reg_number} deactivated` : `${v.reg_number} reactivated`);
      load();
    } catch (err) {
      flash(err.response?.data?.message || 'Could not change vehicle status', true);
    }
  };

  const save = async e => {
    e.preventDefault(); setSaving(true);
    try {
      editing ? await vehicleAPI.update(editing.id, form) : await vehicleAPI.create(form);
      setFormModal(false);
      load();
      if (activeTab === 'expiry') loadExpiring();
      flash(editing ? 'Vehicle updated' : 'Vehicle added');
    } catch (err) {
      flash(err.response?.data?.message || 'Save failed', true);
    } finally { setSaving(false); }
  };

  // ── Detail modal ────────────────────────────────────────────────────────────
  const openDetail = async v => {
    try {
      const r = await vehicleAPI.getOne(v.id);
      setSelected(r.data.data);
      setDetailModal(true);
    } catch { flash('Failed to load vehicle detail', true); }
  };

  // ── Upload modal ────────────────────────────────────────────────────────────
  const openUpload = v => { setSelected(v); setPdfFile(null); setImageFiles([]); setUploadModal(true); };

  const handleUpload = async () => {
    if (!pdfFile && imageFiles.length === 0) return flash('Select at least one file', true);
    const images = selected?.images ? JSON.parse(selected.images) : [];
    if (imageFiles.length + images.length > 4)
      return flash(`Can only store 4 images. Currently have ${images.length}. Select max ${4 - images.length}.`, true);

    setUploading(true);
    const fd = new FormData();
    if (pdfFile) fd.append('cr_book_pdf', pdfFile);
    imageFiles.forEach(f => fd.append('images', f));
    try {
      await vehicleAPI.uploadFiles(selected.id, fd);
      setUploadModal(false);
      load();
      flash('Files uploaded successfully');
      // refresh detail if open
      if (detailModal) {
        const r = await vehicleAPI.getOne(selected.id);
        setSelected(r.data.data);
      }
    } catch (err) {
      flash(err.response?.data?.message || 'Upload failed', true);
    } finally { setUploading(false); }
  };

  const deleteImage = async (vehicleId, imgPath) => {
    const filename = imgPath.split('/').pop();
    if (!window.confirm('Remove this image?')) return;
    try {
      await vehicleAPI.deleteImage(vehicleId, filename);
      const r = await vehicleAPI.getOne(vehicleId);
      setSelected(r.data.data);
      load();
      flash('Image removed');
    } catch { flash('Failed to remove image', true); }
  };

  const remove = async id => {
    const v = vehicles.find(x => x.id === id);
    if (!window.confirm(
      `Delete ${v?.reg_number || 'this vehicle'} permanently?\n\n` +
      'Only vehicles with no history can be deleted. If it has assignments, ' +
      'repairs, fuel logs or documents, deactivate it (🚫) instead — that keeps ' +
      'the records but blocks any further use.'
    )) return;
    try { await vehicleAPI.remove(id); load(); flash('Vehicle deleted'); }
    catch (err) { flash(err.response?.data?.message || 'Delete failed', true); }
  };

  const getImages = v => {
    try { return v?.images ? JSON.parse(v.images) : []; } catch { return []; }
  };

  // ── Expiry status pill ──────────────────────────────────────────────────────
  const expStatusBadge = status => {
    const map = {
      expired:  'bg-red-100 text-red-700 border-red-200',
      critical: 'bg-orange-100 text-orange-700 border-orange-200',
      warning:  'bg-amber-100 text-amber-700 border-amber-200',
      ok:       'bg-green-100 text-green-700 border-green-200',
    };
    const labels = { expired:'Expired', critical:'< 30 days', warning:'< 60 days', ok:'OK' };
    return (
      <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${map[status]||''}`}>
        {labels[status]||status}
      </span>
    );
  };

  return (
    <div>
      <PageHeader
        title="Vehicles"
        sub={`${vehicles.length} vehicles`}
        action={can('admin','transport_officer') && (
          <Button onClick={openAdd}>+ Add Vehicle</Button>
        )}
      />

      {error   && <div className="mb-4"><Alert type="error"   message={error}   /></div>}
      {success && <div className="mb-4"><Alert type="success" message={success} /></div>}

      {/* Tabs */}
      <div className="flex gap-1 mb-5 bg-gray-100 p-1 rounded-xl w-fit">
        {[['list','Vehicle List'], ['expiry','⚠ Expiry Alerts']].map(([t,l]) => (
          <button key={t} onClick={() => setActiveTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all
              ${activeTab === t ? 'bg-white text-primary-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}>
            {l}
          </button>
        ))}
      </div>

      {/* ── TAB: Vehicle List ── */}
      {activeTab === 'list' && (
        <>
          <div className="flex flex-wrap gap-3 mb-5">
            <input value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search reg, brand, model…"
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg w-64 outline-none focus:ring-2 focus:ring-primary-300" />
            <select value={filterStatus} onChange={e => setFilterStatus(e.target.value)}
              className="px-3 py-2 text-sm border border-gray-300 rounded-lg outline-none bg-white focus:ring-2 focus:ring-primary-300">
              <option value="">All statuses</option>
              {['active','inactive','under_repair','assigned','sold','transferred'].map(s => (
                <option key={s} value={s}>{s.replace('_',' ')}</option>
              ))}
            </select>
          </div>

          {loading ? <Loading /> : (
            <Table headers={['Reg No.','Brand/Model','Type','Year','Status','Insurance','Rev. Licence','Emission','Actions']}>
              {vehicles.map(v => (
                <tr key={v.id} className="hover:bg-gray-50 cursor-pointer" onClick={() => openDetail(v)}>
                  <td className="px-4 py-3 font-mono font-semibold text-primary-600 text-sm">{v.reg_number}</td>
                  <td className="px-4 py-3">
                    <p className="text-sm font-medium">{v.brand} {v.model}</p>
                    <p className="text-xs text-gray-400">{v.color || ''}</p>
                  </td>
                  <td className="px-4 py-3 text-sm capitalize">{v.type}</td>
                  <td className="px-4 py-3 text-sm">{v.year || '—'}</td>
                  <td className="px-4 py-3"><StatusBadge status={v.status} /></td>
                  <td className="px-4 py-3"><ExpiryBadge date={v.insurance_expiry} /></td>
                  <td className="px-4 py-3"><ExpiryBadge date={v.revenue_licence_expiry} /></td>
                  <td className="px-4 py-3"><ExpiryBadge date={v.emission_test_expiry} /></td>
                  <td className="px-4 py-3" onClick={e => e.stopPropagation()}>
                    <div className="flex gap-1.5">
                      {can('admin','transport_officer') && (
                        <>
                          <Button variant="ghost" size="sm" onClick={() => openEdit(v)}>Edit</Button>
                          <Button variant="ghost" size="sm" onClick={() => openUpload(v)}>📎</Button>
                          <Button variant="ghost" size="sm"
                            title={v.status === 'inactive' ? 'Reactivate vehicle' : 'Deactivate — blocks assignments, repairs and fuel'}
                            onClick={() => toggleActive(v)}>
                            {v.status === 'inactive' ? '✅' : '🚫'}
                          </Button>
                        </>
                      )}
                      {can('admin') && (
                        <Button variant="danger" size="sm" onClick={() => remove(v.id)}>Del</Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          )}
        </>
      )}

      {/* ── TAB: Expiry Alerts ── */}
      {activeTab === 'expiry' && (
        <>
          {expLoading ? <Loading /> : (
            expiring.length === 0 ? (
              <div className="flex items-center justify-center h-40 bg-white rounded-xl border border-gray-200">
                <p className="text-green-600 font-medium">✓ All documents valid — nothing expiring within 90 days</p>
              </div>
            ) : (
              <Table headers={['Vehicle','Brand/Model','Document','Doc. No.','Expiry Date','Days Left','Status','Action']}>
                {expiring.map((e, i) => (
                  <tr key={i} className={`hover:bg-gray-50 ${e.status === 'expired' ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3 font-mono font-semibold text-primary-600 text-sm">{e.reg_number}</td>
                    <td className="px-4 py-3 text-sm">{e.brand} {e.model}</td>
                    <td className="px-4 py-3 text-sm font-medium">{e.doc_type}</td>
                    <td className="px-4 py-3 text-xs text-gray-500">{e.doc_no || '—'}</td>
                    <td className="px-4 py-3 text-sm">{e.expiry_date}</td>
                    <td className="px-4 py-3">
                      <span className={`text-sm font-bold ${e.days_left < 0 ? 'text-red-600' : e.days_left <= 30 ? 'text-orange-600' : 'text-amber-600'}`}>
                        {e.days_left < 0 ? `${Math.abs(e.days_left)}d overdue` : `${e.days_left} days`}
                      </span>
                    </td>
                    <td className="px-4 py-3">{expStatusBadge(e.status)}</td>
                    <td className="px-4 py-3">
                      {can('admin','transport_officer') && (
                        <Button variant="ghost" size="sm"
                          onClick={() => { const v = vehicles.find(x => x.id === e.vehicle_id); if (v) openEdit(v); }}>
                          Update
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </Table>
            )
          )}
        </>
      )}

      {/* ══ ADD / EDIT MODAL ══════════════════════════════════════════════════ */}
      <Modal open={formModal} onClose={() => setFormModal(false)}
        title={editing ? `Edit — ${editing.reg_number}` : 'Add Vehicle'} size="xl">
        <form onSubmit={save} className="space-y-5">

          {/* Basic info */}
          <div>
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Basic Information</p>
            <div className="grid grid-cols-2 gap-4">
              <Input label="Reg Number *" value={form.reg_number}
                onChange={e => setForm({...form, reg_number: e.target.value})} required
                placeholder="e.g. WP-KY-2525" />
              <Select label="Type *" value={form.type}
                onChange={e => setForm({...form, type: e.target.value})} required>
                {TYPES.map(t => <option key={t} value={t}>{t}</option>)}
              </Select>
              <Input label="Brand" value={form.brand}
                onChange={e => setForm({...form, brand: e.target.value})} placeholder="e.g. Toyota" />
              <Input label="Model" value={form.model}
                onChange={e => setForm({...form, model: e.target.value})} placeholder="e.g. Hilux" />
              <Input label="Year" type="number" value={form.year}
                onChange={e => setForm({...form, year: e.target.value})} placeholder="2023" />
              <Input label="Color" value={form.color}
                onChange={e => setForm({...form, color: e.target.value})} placeholder="White" />
              <Select label="Fuel Type" value={form.fuel_type}
                onChange={e => setForm({...form, fuel_type: e.target.value})}>
                {list('fuel_type', FUELS).map(f => <option key={f} value={f}>{f}</option>)}
              </Select>
              <Select label="Ownership (Company) *" value={form.company_id}
                onChange={e => setForm({...form, company_id: e.target.value})}>
                <option value="">Select company…</option>
                {companies.map(c => (
                  <option key={c.id} value={c.id}>{c.name}{c.short_name ? ` (${c.short_name})` : ''}</option>
                ))}
              </Select>
              <Input label="Engine Number" value={form.engine_number}
                onChange={e => setForm({...form, engine_number: e.target.value})} />
              <Input label="Chassis Number" value={form.chassis_number}
                onChange={e => setForm({...form, chassis_number: e.target.value})} />
              <Input label="Odometer (km)" type="number" value={form.odometer}
                onChange={e => setForm({...form, odometer: e.target.value})} />
              <div className="col-span-2">
                <label className="flex items-center gap-2 px-3 py-2.5 rounded-lg border cursor-pointer text-sm"
                  style={form.has_lease
                    ? { borderColor: '#F59E0B', background: '#FFFBEB' }
                    : { borderColor: '#E5E7EB' }}>
                  <input type="checkbox" checked={!!form.has_lease}
                    onChange={e => setForm({...form, has_lease: e.target.checked, ownership: e.target.checked ? 'leased' : 'owned' })}
                    className="w-4 h-4" />
                  🏦 This vehicle is on lease — show lease details
                </label>
                {companies.length === 0 && (
                  <p className="text-xs text-amber-600 mt-1">
                    ⚠ No companies yet — add them in Settings → Companies first.
                  </p>
                )}
              </div>
            </div>
          </div>

          {/* CR / Registration Book Details */}
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-xs font-semibold text-purple-700 uppercase tracking-wide mb-3">📕 CR / Registration Book Details</p>
            <div className="grid grid-cols-2 gap-4">
              <Input label="CR No." value={form.cr_no}
                onChange={e => setForm({...form, cr_no: e.target.value})} />
              <Select label="Class of Vehicle" value={form.vehicle_class}
                onChange={e => setForm({...form, vehicle_class: e.target.value})}>
                <option value="">Select…</option>
                {list('vehicle_class', []).map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Select label="Status when Registered" value={form.status_when_registered}
                onChange={e => setForm({...form, status_when_registered: e.target.value})}>
                <option value="">Select…</option>
                {list('reg_status', REG_STATUS).map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Select label="Type of Body" value={form.body_type}
                onChange={e => setForm({...form, body_type: e.target.value})}>
                <option value="">Select…</option>
                {list('body_type', BODY_TYPES).map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Input label="Date of First Registration" type="date" value={form.first_reg_date || ''}
                onChange={e => setForm({...form, first_reg_date: e.target.value})} />
              <Input label="Cylinder Capacity (cc)" value={form.cylinder_capacity}
                onChange={e => setForm({...form, cylinder_capacity: e.target.value})} placeholder="e.g. 2400" />
              <Select label="Tax Class" value={form.tax_class}
                onChange={e => setForm({...form, tax_class: e.target.value})}>
                <option value="">Select…</option>
                {list('tax_class', []).map(o => <option key={o} value={o}>{o}</option>)}
              </Select>
              <Input label="Country of Origin" value={form.country_of_origin}
                onChange={e => setForm({...form, country_of_origin: e.target.value})} placeholder="e.g. Japan" />
              <div className="col-span-2">
                <Input label="Manufacturer's Description" value={form.manufacturer_description}
                  onChange={e => setForm({...form, manufacturer_description: e.target.value})} />
              </div>
              <div className="col-span-2 grid grid-cols-3 gap-4 pt-3 border-t border-purple-200">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tyre Size</label>
                  <input list="v-tyre-list" value={form.tyre_size || ''}
                    onChange={e => setForm({...form, tyre_size: e.target.value})}
                    placeholder={tyreSizes.length ? 'Select or type…' : 'e.g. 265/65R17'}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300" />
                  <datalist id="v-tyre-list">
                    {tyreSizes.map(t => <option key={t.id} value={t.tyre_size} />)}
                  </datalist>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Battery Size</label>
                  <input list="v-bat-list" value={form.battery_size || ''}
                    onChange={e => setForm({...form, battery_size: e.target.value})}
                    placeholder={batSizes.length ? 'Select or type…' : 'e.g. 12V70A'}
                    className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white outline-none focus:ring-2 focus:ring-primary-300" />
                  <datalist id="v-bat-list">
                    {batSizes.map(b => <option key={b.id} value={b.bat_size} />)}
                  </datalist>
                </div>
                <Input label="Battery Count" type="number" min="0" value={form.battery_count}
                  onChange={e => setForm({...form, battery_count: e.target.value})} placeholder="e.g. 1" />
              </div>
            </div>
          </div>

          {/* Lease Details — shown when ownership is leased */}
          {form.has_lease && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
              <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide mb-3">🏦 Lease Details</p>
              <div className="grid grid-cols-2 gap-4">
                <Input label="Lease Provider" value={form.lease_provider}
                  onChange={e => setForm({...form, lease_provider: e.target.value})} placeholder="e.g. LOLC, People's Leasing" />
                <Input label="Lease Amount (Rs.)" type="number" step="0.01" value={form.lease_amount}
                  onChange={e => setForm({...form, lease_amount: e.target.value})} />
                <Input label="Duration" value={form.lease_duration}
                  onChange={e => setForm({...form, lease_duration: e.target.value})} placeholder="e.g. 60 months" />
                <Input label="Lease Start Date" type="date" value={form.lease_start_date || ''}
                  onChange={e => setForm({...form, lease_start_date: e.target.value})} />
                <Input label="Lease End Date" type="date" value={form.lease_end_date || ''}
                  onChange={e => setForm({...form, lease_end_date: e.target.value})} />
                <Select label="Lease Status" value={form.lease_status || 'active'}
                  onChange={e => setForm({...form, lease_status: e.target.value})}>
                  <option value="active">Active</option>
                  <option value="closed">Closed</option>
                </Select>
                <Select label="Is CR Transferred?" value={String(form.cr_transferred ?? '')}
                  onChange={e => setForm({...form, cr_transferred: e.target.value === '' ? null : e.target.value === 'true'})}>
                  <option value="">—</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </Select>
              </div>
            </div>
          )}

          {/* Documents are managed in the Renewals menu (with full history) */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
            📄 <strong>Documents &amp; renewals</strong> — insurance, revenue licence, emission and fitness
            certificates are now managed in the <strong>Renewals</strong> menu, where each renewal is
            saved with its period, amount and payment details so you keep full history.
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
            <Button type="button" variant="ghost" onClick={() => setFormModal(false)}>Cancel</Button>
            <Button type="submit" disabled={saving}>
              {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Vehicle'}
            </Button>
          </div>

        </form>
      </Modal>

      {/* ══ DETAIL MODAL ══════════════════════════════════════════════════════ */}
      <Modal open={detailModal} onClose={() => setDetailModal(false)}
        title={`${selected?.reg_number} — ${selected?.brand} ${selected?.model}`} size="xl">
        {selected && (
          <div className="space-y-5">
            {/* Images */}
            {getImages(selected).length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Photos</p>
                <div className="grid grid-cols-4 gap-2">
                  {getImages(selected).map((img, i) => (
                    <div key={i} className="relative group">
                      <img src={`${API_BASE}${img}`} alt={`Vehicle ${i+1}`}
                        className="w-full h-24 object-cover rounded-lg border border-gray-200" />
                      {can('admin','transport_officer') && (
                        <button onClick={() => deleteImage(selected.id, img)}
                          className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs
                                     opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          ×
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CR Book */}
            {selected.cr_book_pdf && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-2xl">📄</span>
                  <div>
                    <p className="text-sm font-medium text-blue-700">CR Book (PDF)</p>
                    <p className="text-xs text-blue-500">{selected.cr_book_pdf.split('/').pop()}</p>
                  </div>
                </div>
                <a href={`${API_BASE}${selected.cr_book_pdf}`} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-600 font-medium hover:underline">
                  View / Download →
                </a>
              </div>
            )}

            {/* Documents grid */}
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-3">Document Status</p>
              <div className="grid grid-cols-2 gap-3">
                {[
                  { label:'🛡 Insurance',          no: selected.insurance_no,         expiry: selected.insurance_expiry },
                  { label:'📋 Revenue Licence',    no: selected.revenue_licence_no,   expiry: selected.revenue_licence_expiry },
                  { label:'🌿 Emission Test',      no: selected.emission_test_no,     expiry: selected.emission_test_expiry },
                  { label:'✅ Fitness Certificate',no: selected.fitness_cert_no,      expiry: selected.fitness_cert_expiry },
                ].map(doc => (
                  <div key={doc.label} className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-semibold text-gray-600 mb-1">{doc.label}</p>
                    <p className="text-xs text-gray-500 mb-1">{doc.no || 'No number recorded'}</p>
                    <ExpiryBadge date={doc.expiry} />
                  </div>
                ))}
              </div>
            </div>

            {/* Basic details */}
            <div className="grid grid-cols-3 gap-3 text-sm">
              {[
                ['Type',       selected.type],
                ['Brand',      selected.brand],
                ['Model',      selected.model],
                ['Year',       selected.year],
                ['Color',      selected.color],
                ['Fuel',       selected.fuel_type],
                ['Ownership',  selected.company?.name || selected.ownership],
                ['Odometer',   selected.odometer ? `${selected.odometer} km` : '—'],
                ['Status',     selected.status],
                ['Engine No.', selected.engine_number],
                ['Chassis No.',selected.chassis_number],
                ['CR No.', selected.cr_no],
                ['Class of Vehicle', selected.vehicle_class],
                ['Status when Registered', selected.status_when_registered],
                ['Type of Body', selected.body_type],
                ['First Registration', selected.first_reg_date],
                ['Cylinder Capacity', selected.cylinder_capacity ? `${selected.cylinder_capacity} cc` : null],
                ['Tax Class', selected.tax_class],
                ['Country of Origin', selected.country_of_origin],
                ['Manufacturer Desc.', selected.manufacturer_description],
                ['Tyre Size', selected.tyre_size],
                ['Battery Size', selected.battery_size],
                ['Battery Count', selected.battery_count],
                ['Owner Company', selected.company?.name],
                ...(selected.has_lease || selected.ownership === 'leased' ? [
                  ['Lease Provider', selected.lease_provider],
                  ['Lease Amount', selected.lease_amount ? `Rs. ${Number(selected.lease_amount).toLocaleString('en-LK')}` : null],
                  ['Lease Duration', selected.lease_duration],
                  ['Lease Start', selected.lease_start_date],
                  ['Lease End', selected.lease_end_date],
                  ['Lease Status', selected.lease_status],
                  ['CR Transferred', selected.cr_transferred === true || selected.cr_transferred === 1 ? 'Yes' : selected.cr_transferred === false || selected.cr_transferred === 0 ? 'No' : null],
                ] : []),
              ].map(([k,v]) => (
                <div key={k} className="bg-gray-50 rounded-lg px-3 py-2">
                  <p className="text-xs text-gray-400">{k}</p>
                  <p className="font-medium text-gray-700 capitalize">{v || '—'}</p>
                </div>
              ))}
            </div>

            {selected.notes && (
              <div className="bg-gray-50 rounded-lg px-3 py-2">
                <p className="text-xs text-gray-400 mb-1">Notes</p>
                <p className="text-sm text-gray-700">{selected.notes}</p>
              </div>
            )}

            <div className="flex justify-end gap-3">
              {can('admin','transport_officer') && (
                <>
                  <Button variant="ghost" onClick={() => { setDetailModal(false); openUpload(selected); }}>
                    📎 Upload Files
                  </Button>
                  <Button onClick={() => { setDetailModal(false); openEdit(selected); }}>
                    Edit Vehicle
                  </Button>
                </>
              )}
            </div>
          </div>
        )}
      </Modal>

      {/* ══ UPLOAD MODAL ══════════════════════════════════════════════════════ */}
      <Modal open={uploadModal} onClose={() => setUploadModal(false)}
        title={`Upload Files — ${selected?.reg_number}`} size="md">
        <div className="space-y-5">

          {/* Existing images */}
          {getImages(selected).length > 0 && (
            <div>
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                Current Photos ({getImages(selected).length}/4)
              </p>
              <div className="grid grid-cols-4 gap-2">
                {getImages(selected).map((img, i) => (
                  <div key={i} className="relative group">
                    <img src={`${API_BASE}${img}`} alt="" className="w-full h-20 object-cover rounded border" />
                    <button onClick={() => deleteImage(selected.id, img)}
                      className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-5 h-5 text-xs
                                 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* CR Book PDF */}
          <div className="border-2 border-dashed border-blue-200 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">📄 CR Book (PDF)</p>
            {selected?.cr_book_pdf ? (
              <div className="flex items-center justify-between bg-blue-50 rounded-lg px-3 py-2 mb-2">
                <span className="text-xs text-blue-600">Current: {selected.cr_book_pdf.split('/').pop()}</span>
                <a href={`${API_BASE}${selected.cr_book_pdf}`} target="_blank" rel="noreferrer"
                  className="text-xs text-blue-600 underline">View</a>
              </div>
            ) : null}
            <input ref={pdfRef} type="file" accept=".pdf" className="hidden"
              onChange={e => setPdfFile(e.target.files[0])} />
            <Button variant="ghost" size="sm" onClick={() => pdfRef.current.click()}>
              {pdfFile ? `✓ ${pdfFile.name}` : (selected?.cr_book_pdf ? 'Replace PDF' : 'Select PDF')}
            </Button>
            <p className="text-xs text-gray-400 mt-1">Max 10MB · PDF only</p>
          </div>

          {/* Vehicle Images */}
          <div className="border-2 border-dashed border-green-200 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-1">
              📷 Vehicle Photos (max 4 total)
            </p>
            <p className="text-xs text-gray-400 mb-3">
              Currently {getImages(selected).length}/4 · Can add {4 - getImages(selected).length} more
            </p>
            <input ref={imgRef} type="file" accept="image/*" multiple className="hidden"
              onChange={e => setImageFiles(Array.from(e.target.files).slice(0, 4 - getImages(selected).length))} />
            <Button variant="ghost" size="sm" onClick={() => imgRef.current.click()}
              disabled={getImages(selected).length >= 4}>
              {imageFiles.length > 0 ? `✓ ${imageFiles.length} file(s) selected` : 'Select Images'}
            </Button>
            {imageFiles.length > 0 && (
              <div className="mt-2 flex flex-wrap gap-1">
                {imageFiles.map((f, i) => (
                  <span key={i} className="text-xs bg-green-100 text-green-700 px-2 py-0.5 rounded">{f.name}</span>
                ))}
              </div>
            )}
            <p className="text-xs text-gray-400 mt-1">JPG, PNG, WEBP · Max 10MB each</p>
          </div>

          <div className="flex justify-end gap-3">
            <Button variant="ghost" onClick={() => setUploadModal(false)}>Cancel</Button>
            <Button onClick={handleUpload} disabled={uploading || (!pdfFile && imageFiles.length === 0)}>
              {uploading ? 'Uploading…' : 'Upload Files'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
